/**
 * F302 V2 — Web push opt-in CTA.
 *
 * Mounted in the NotificationBell. Shows when:
 *   - Browser supports PushManager + Notification API
 *   - VAPID is configured server-side (we fetch /api/push/public-key)
 *   - User hasn't already granted (Notification.permission === 'default')
 *
 * On click:
 *   1. Request notification permission
 *   2. If granted, register a PushManager subscription with the VAPID public key
 *   3. POST the subscription to /api/push/subscribe
 *   4. Hide the CTA + show a "subscribed" toast
 *
 * Self-hides cleanly when the browser doesn't support push (Safari iOS
 * before 16.4, older Firefox flavors) — no error, just doesn't appear.
 */

import React, { useEffect, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import api from '../api';
import { useToast } from '../hooks/useToast';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported(): boolean {
  return (
    'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
  );
}

export default function PushNotificationOptIn(): React.ReactElement | null {
  const { showToast } = useToast();
  const [vapidKey, setVapidKey] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  // Mount-time setup: check browser support + VAPID config + existing subscription.
  useEffect(() => {
    if (!pushSupported()) return;
    setPermission(Notification.permission);
    void (async () => {
      try {
        // Fetch the VAPID public key. If unset on server (empty string),
        // the CTA stays hidden — server-side admin needs to set it first.
        const res = await api.get('/api/push/public-key');
        const data = res.data as { public_key: string; configured: boolean };
        if (data.configured && data.public_key) {
          setVapidKey(data.public_key);
        }
        // Also check if this browser already has an active subscription.
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) setAlreadySubscribed(true);
      } catch {
        // Silent — backend not reachable or push not configured
      }
    })();
  }, []);

  const handleEnable = async () => {
    if (!vapidKey) return;
    setSubscribing(true);
    try {
      // 1. Request notification permission (browser prompt)
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        showToast('Push notifications declined.', 'warning');
        return;
      }

      // 2. Register the push subscription with the server's VAPID public key
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      // 3. Send the subscription to our backend so it can fan out push
      // notifications via pywebpush.
      const subJson = sub.toJSON();
      await api.post('/api/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setAlreadySubscribed(true);
      showToast('Push notifications enabled on this device.', 'success');
    } catch (e: any) {
      showToast(
        e?.message || 'Could not enable push notifications. Try again from the bell menu.',
        'error',
      );
    } finally {
      setSubscribing(false);
    }
  };

  // Visibility rules — hide unless we can actually offer push.
  if (!pushSupported()) return null;
  if (!vapidKey) return null;                  // server not configured
  if (permission === 'denied') return null;    // user already blocked; can't re-prompt
  if (alreadySubscribed) return null;          // already subscribed on this device

  return (
    <div style={{
      padding: '10px 14px', margin: '8px 12px',
      background: '#f0f9ff', border: '1px solid #bae6fd',
      borderRadius: 6, fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Bell size={14} style={{ color: '#0369a1' }} />
        <strong style={{ color: '#0c4a6e' }}>Get push notifications</strong>
      </div>
      <p style={{ margin: '0 0 8px', color: '#075985', fontSize: 12 }}>
        Get a ping on this device when your published proposals get opened, decisions are logged, or your team @-mentions you.
      </p>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handleEnable}
        disabled={subscribing}
        style={{ padding: '4px 12px', fontSize: 12 }}
      >
        {subscribing ? 'Enabling…' : 'Enable on this device'}
      </button>
    </div>
  );
}
